import "@/components/Interface/FormFields/RadioButton/RadioButton.scss";

import { RadioGroup } from "@headlessui/react";
import clsx from "clsx";
import { ReactNode } from "react";
import {
  FieldErrors,
  FieldValue,
  FieldValues,
  Path,
  RegisterOptions,
  UseFormRegister,
} from "react-hook-form";

import { getNestedValue } from "@/utils/objectUtils";

export interface RadioButtonProps<TFormValues extends FieldValues> {
  id?: string;
  className?: string;
  name: Path<TFormValues>;
  value: string | number;
  label?: string;
  labelComponent?: ReactNode;
  options?:
    | (RegisterOptions & { readOnly?: boolean })
    | (Omit<RegisterOptions, "disabled"> & {
        readOnly?: boolean;
        disabled: false;
      });
  errors: FieldErrors;
  disabled?: boolean;
  register: UseFormRegister<TFormValues>;
  isSelected?: boolean;
}

export const RadioButton = <TFormValues extends FieldValues>({
  id,
  className,
  name,
  value,
  label,
  labelComponent,
  options = { required: true },
  errors,
  register,
  isSelected,
}: RadioButtonProps<TFormValues>) => {
  const uid = id ?? `${name}-${value}`;

  // Use getNestedValue to safely access nested field errors
  const fieldError = getNestedValue(errors, name);

  const hasError = fieldError !== undefined;

  return (
    <div className={clsx("radio-button-container", className)}>
      <div className="input-wrapper">
        <input
          // This cannot be options.disabled, as this would pass the option into ...register above,
          // which then removes any value from this field.
          {...(options.readOnly && { disabled: true })}
          id={uid}
          className={clsx("min-w-max", hasError && "error")}
          type="radio"
          value={value}
          aria-label={label}
          data-testid={uid}
          tabIndex={0}
          checked={isSelected}
          {...register(name, options as RegisterOptions<TFormValues>)}
        />
        <div className="radio-button" />
      </div>
      <label
        htmlFor={uid}
        className={clsx(
          "label",
          options?.disabled || options?.readOnly
            ? "cursor-not-allowed opacity-20"
            : "cursor-pointer",
        )}
      >
        {label}
        {labelComponent}
      </label>
    </div>
  );
};

/**
 * Every disabled/checked/active × inverted branch in this file picks one of
 * four tokens the same way - `state` for the semantic condition (disabled,
 * checked, active), `inverted` for dark-surface mode.
 */
const pickToken = (
  state: boolean,
  inverted: boolean,
  tokens: {
    state: string;
    stateInverted: string;
    base: string;
    baseInverted: string;
  },
) =>
  state
    ? inverted
      ? tokens.stateInverted
      : tokens.state
    : inverted
      ? tokens.baseInverted
      : tokens.base;

export interface RadioIndicatorProps {
  checked: boolean;
  disabled?: boolean;
  /** The option's own value fails validation, independent of the group's disabled state. */
  invalid?: boolean;
  active?: boolean;
  inverted?: boolean;
  className?: string;
}

export const RadioIndicator = ({
  checked,
  disabled = false,
  invalid = false,
  active = false,
  inverted = false,
  className,
}: RadioIndicatorProps) => (
  <div
    aria-hidden="true"
    className={clsx(
      "relative size-6 shrink-0 rounded-full border-2 transition-colors",
      disabled &&
        pickToken(checked, inverted, {
          state: "border-transparent bg-surface-secondary",
          stateInverted: "border-transparent bg-surface-secondary-inverse",
          base: "border-border-tertiary",
          baseInverted: "border-border-tertiary-inverse",
        }),
      !disabled &&
        invalid &&
        pickToken(checked, inverted, {
          state: "border-transparent bg-foreground-error",
          stateInverted: "border-transparent bg-surface-error-inverse",
          base: "border-foreground-error",
          baseInverted: "border-error-inverse",
        }),
      !disabled &&
        !invalid &&
        (checked
          ? inverted
            ? "border-transparent bg-surface-primary-inverse"
            : "border-transparent bg-surface-primary"
          : clsx(
              inverted
                ? "hover:border-foreground-primary-inverse"
                : "hover:border-foreground-primary",
              pickToken(active, inverted, {
                state: "border-foreground-primary",
                stateInverted: "border-foreground-primary-inverse",
                base: "border-border-secondary",
                baseInverted: "border-border-secondary-inverse",
              }),
            )),
      className,
    )}
  >
    {checked && (
      <div
        className={clsx(
          "absolute left-1/2 top-1/2 size-2 -translate-x-1/2 -translate-y-1/2 rounded-full",
          pickToken(disabled, inverted, {
            state: "bg-foreground-tertiary",
            stateInverted: "bg-foreground-tertiary-inverse",
            base: "bg-foreground-primary-inverse",
            baseInverted: "bg-foreground-primary",
          }),
        )}
      />
    )}
    {active && !disabled && (
      <div
        className={clsx(
          "absolute left-1/2 top-1/2 size-8 -translate-x-1/2 -translate-y-1/2 rounded-full border-2",
          inverted
            ? "border-foreground-primary-inverse"
            : "border-foreground-primary",
        )}
      />
    )}
  </div>
);

export interface RadioButtonOptionProps<TFormValues extends FieldValues> {
  name: Path<TFormValues>;
  label: ReactNode;
  value: FieldValue<TFormValues>;
  disabled?: boolean;
  invalid?: boolean;
  inverted?: boolean;
  /** Rendered between the circle and the label, e.g. a payment provider logo. */
  icon?: ReactNode;
}

/**
 * `dire-radio` option (label + circle) for a headlessui `RadioGroup`. For a
 * circle without a plain-text label next to it (a whole card, a table row),
 * use `RadioIndicator` directly instead.
 */
export const RadioButtonOption = <TFormValues extends FieldValues>({
  name,
  label,
  value,
  disabled = false,
  invalid = false,
  inverted = false,
  icon,
}: RadioButtonOptionProps<TFormValues>) => {
  const uid = `${name}-${value}`;

  return (
    <RadioGroup.Option
      id={uid}
      value={value}
      disabled={disabled}
      data-testid={uid}
      className={({ disabled: isDisabled }) =>
        clsx(
          "flex items-center gap-3 outline-none",
          isDisabled ? "cursor-not-allowed" : "cursor-pointer",
        )
      }
    >
      {({ checked, active }) => (
        <>
          <RadioIndicator
            checked={checked}
            disabled={disabled}
            invalid={invalid}
            active={active}
            inverted={inverted}
          />
          {icon}
          {/* RadioGroup.Label, not a plain <label htmlFor>: the option it
              would point at is a <div>, non-labellable by the HTML spec.
              Nesting Label here (rather than at the group) auto-wires this
              option's own aria-labelledby via headlessui's per-option label
              context. */}
          <RadioGroup.Label
            as="span"
            className={clsx(
              "text-body-base font-semibold leading-md",
              pickToken(disabled, inverted, {
                state: "text-foreground-tertiary",
                stateInverted: "text-foreground-tertiary-inverse",
                base: "text-foreground-primary",
                baseInverted: "text-foreground-primary-inverse",
              }),
            )}
          >
            {label}
          </RadioGroup.Label>
        </>
      )}
    </RadioGroup.Option>
  );
};
